import * as dashboardService from '../../../services/api/new-dashboard-api';
import * as taskDynamicFieldsUtil from '../../../components/DynamicField/Tasks/utils/utils';
import { SORT_DIRECTIONS } from './dynamic-fields-table/dashboard-dynamic-fields-table';
import { isDate } from '../dashboard-util/dashboard-utils';
import dayjs from '../../../util/dayjs-config';
import { insertAt } from '../../../util/array-helpers';

export const SORT_TYPES = {
    currentWeek: 'Current Week',
    allTime: 'All Time',
    custom: 'Custom',
};

// only include Current Week for now
export const SORT_TYPES_ARRAY = Object.values(SORT_TYPES).slice(0, 1);

export const cellPlaceholder = 'N/A';

export const sortRows = ({ fieldType, sortColumnIdx, sortDirection, rows }) => {
    if (sortColumnIdx === SORT_DIRECTIONS.NONE || sortDirection === SORT_DIRECTIONS.NONE) {
        return rows;
    }
    return [...rows].sort((rowA, rowB) => {
        let compare = 0;
        // sort by member/project
        if (sortColumnIdx === 0) {
            compare = String(rowA[0]).localeCompare(String(rowB[0]));
        }
        // sort by field
        else if (fieldType === taskDynamicFieldsUtil.FIELD_TYPES.time) {
            compare = compareTimeData(rowA[sortColumnIdx], rowB[sortColumnIdx]);
        }
        else if(fieldType == taskDynamicFieldsUtil.FIELD_TYPES.date){
                // isDate() returns the detected format string (e.g. "MM/DD/YYYY") or false.
                // Use strict parsing with the format when detected; fall back to dayjs's default
                // parser otherwise. Previously this passed a raw boolean to moment as the format
                // arg, which moment silently ignored.
                let isDateA = isDate(rowA[sortColumnIdx]);
                let isDateB = isDate(rowB[sortColumnIdx]);
                let dayA = isDateA ? dayjs(rowA[sortColumnIdx], isDateA, true) : dayjs(rowA[sortColumnIdx]);
                let dayB = isDateB ? dayjs(rowB[sortColumnIdx], isDateB, true) : dayjs(rowB[sortColumnIdx]);

                let dateA = new Date(0);
                let dateB = new Date(0);
                if(dayA.isValid()){
                    dateA = dayA.toDate();
                }

                if(dayB.isValid()){
                    dateB = dayB.toDate();
                }
                if (dateA < dateB) {
                    compare= -1;
                  }
                else if (dateA > dateB) {
                    compare= 1;
                }
        }

        return sortDirection === SORT_DIRECTIONS.UP ? -compare : compare;
    });
};

/**
 * Sorts rows along with rowIds so the ID for each row stays aligned with its data.
 * If rowIds is missing, we fall back to the old `sortRows`-like approach but generate a dummy rowIds array.
 */
export const sortRowsWithIds = ({
    fieldType,
    sortColumnIdx,
    sortDirection,
    rows,
    rowIds,
  }) => {
    // 1) Handle empty/no-sorting scenarios
    if (
      sortColumnIdx === SORT_DIRECTIONS.NONE ||
      sortDirection === SORT_DIRECTIONS.NONE ||
      !rows?.length
    ) {
      // If rowIds is missing or not the right length, generate a fallback
      if (!rowIds || rowIds.length !== rows?.length) {
        return {
          sortedRows: rows || [],
          sortedRowIds: (rows || []).map((_, i) => i),
        };
      }
      // Otherwise, just return as is
      return { sortedRows: rows, sortedRowIds: rowIds };
    }

    // 2) If rowIds is missing (or length mismatch), fall back to "old" sortRows logic
    //    and build a dummy rowIds array
    if (!rowIds || rowIds.length !== rows.length) {
      // do the same sorting logic you have in `sortRows`
      const sortedRows = [...rows].sort((rowA, rowB) => {
        let compare = 0;

        // Sort by member/project if column 0
        if (sortColumnIdx === 0) {
          compare = String(rowA[0]).localeCompare(String(rowB[0]));
        }
        // Sort by time field
        else if (fieldType === taskDynamicFieldsUtil.FIELD_TYPES.time) {
          compare = compareTimeData(rowA[sortColumnIdx], rowB[sortColumnIdx]);
        }
        // Sort by date field
        else if (fieldType === taskDynamicFieldsUtil.FIELD_TYPES.date) {
          // See note in sortRows about strict parsing with the detected format.
          const isDateA = isDate(rowA[sortColumnIdx]);
          const isDateB = isDate(rowB[sortColumnIdx]);
          const dayA = isDateA ? dayjs(rowA[sortColumnIdx], isDateA, true) : dayjs(rowA[sortColumnIdx]);
          const dayB = isDateB ? dayjs(rowB[sortColumnIdx], isDateB, true) : dayjs(rowB[sortColumnIdx]);

          let dateA = new Date(0);
          let dateB = new Date(0);
          if (dayA.isValid()) {
            dateA = dayA.toDate();
          }
          if (dayB.isValid()) {
            dateB = dayB.toDate();
          }
          if (dateA < dateB) {
            compare = -1;
          } else if (dateA > dateB) {
            compare = 1;
          }
        }

        return sortDirection === SORT_DIRECTIONS.UP ? -compare : compare;
      });

      // generate a dummy ID array if needed
      const fallbackRowIds = sortedRows.map((_, i) => i);
      return { sortedRows, sortedRowIds: fallbackRowIds };
    }

    // 3) If we have valid rowIds, do the (row, rowId) combined sort
    const combined = rows.map((row, i) => ({
      row,
      rowId: rowIds[i],
    }));

    combined.sort((a, b) => {
      let compare = 0;
      const rowA = a.row;
      const rowB = b.row;

      // same logic from the old `sortRows`
      if (sortColumnIdx === 0) {
        compare = String(rowA[0]).localeCompare(String(rowB[0]));
      } else if (fieldType === taskDynamicFieldsUtil.FIELD_TYPES.time) {
        compare = compareTimeData(rowA[sortColumnIdx], rowB[sortColumnIdx]);
      } else if (fieldType === taskDynamicFieldsUtil.FIELD_TYPES.date) {
        // See note in sortRows about strict parsing with the detected format.
        const isDateA = isDate(rowA[sortColumnIdx]);
        const isDateB = isDate(rowB[sortColumnIdx]);
        const dayA = isDateA ? dayjs(rowA[sortColumnIdx], isDateA, true) : dayjs(rowA[sortColumnIdx]);
        const dayB = isDateB ? dayjs(rowB[sortColumnIdx], isDateB, true) : dayjs(rowB[sortColumnIdx]);

        let dateA = new Date(0);
        let dateB = new Date(0);
        if (dayA.isValid()) {
          dateA = dayA.toDate();
        }
        if (dayB.isValid()) {
          dateB = dayB.toDate();
        }
        if (dateA < dateB) {
          compare = -1;
        } else if (dateA > dateB) {
          compare = 1;
        }
      }

      // Ascending vs descending
      return sortDirection === SORT_DIRECTIONS.UP ? -compare : compare;
    });

    const sortedRows = combined.map(obj => obj.row);
    const sortedRowIds = combined.map(obj => obj.rowId);

    return { sortedRows, sortedRowIds };
};

export const formatDashboardDynamicFields = ({ sortType, dynamicFields, customDates }) => {
    const { fieldType, fields, data, ownerType } = dynamicFields;

    let oldcolumns = [];
    let columns = [];
    const rows = [];
    let rowsRolledOver = [];
    if (fieldType === taskDynamicFieldsUtil.FIELD_TYPES.time) {
        oldcolumns.push(ownerType, ...fields.map((field) => field.LabelShort));
        columns = insertAt(oldcolumns, 3, "Total Hours");
        columns.push("Last Updated");
        rowsRolledOver.push(
            ...data.map((row) => {
                let rowOldData = false;
                let rowRO = [
                    false,
                    ...fields.map((dd) => {
                        const foundData = row?.DynamicFields?.find(
                            (rawData) => dd?.Id === rawData?.DynamicFieldId
                        );

                        let isRolledOver = false;

                        if (foundData?.DynamicDatas?.length > 0) {
                            isRolledOver = foundData?.DynamicDatas[0]?.IsOldData;
                        }
                        if (isRolledOver) {
                            rowOldData = true;
                        }
                        return isRolledOver;
                    })
                ];
                rowRO.push(rowOldData);
                return rowRO;
            })
        );

        rows.push(
            ...data.map((row) => {
                const totalHoursRows = [];
                let cells = [
                    row.Owner.Name,
                    ...fields.map((field) => {
                        const foundData = row?.DynamicFields?.find(
                            (rawData) => field?.Id === rawData?.DynamicFieldId
                        );

                        if (!foundData) return cellPlaceholder;

                        const { DynamicDatas } = foundData;

                        if (
                            field.LabelShort === 'Weekend Hours' ||
                            field.LabelShort === 'Last Week'
                        ) {
                            totalHoursRows.push(...DynamicDatas);
                        }
                        if(field.LabelShort?.toLowerCase() === "mds" ||
                                field.LabelShort?.toLowerCase() === "directors" ||
                                field.LabelShort?.toLowerCase() === "vps")
                        {
                            return foundData?.DynamicDatas[0]?.DynamicData.Data ?? "";
                        }
                        else {
                            return formatTimeDynamicData({
                                sortType,
                                DynamicDatas,
                                customDates,
                            });
                        }
                    }),
                ];

                cells = insertAt(cells, 3, formatTimeDynamicData({sortType,DynamicDatas: totalHoursRows,customDates}));
                let lastDateString = row.DynamicFields[0]?.DynamicDatas[0]?.CreatedDateString;
                let lastDate = dayjs(row.DynamicFields[0]?.DynamicDatas[0]?.CreatedDate);

                for (let i = 0; i < row.DynamicFields.length; i++) {
                    for(let j = 0; j < row.DynamicFields[i]?.DynamicDatas.length; j++){
                        let rowData = row.DynamicFields[i]?.DynamicDatas[j];
                        let rowDateDay = dayjs(rowData?.CreatedDate);
                         if (rowDateDay.isValid() && (!lastDate.isValid() || lastDate.isBefore(rowDateDay))) {
                            lastDate = rowDateDay;
                            lastDateString = rowData?.CreatedDateString;
                        }
                    }
                }
                cells.push(lastDateString ?? "-");
                return cells;
            })
        );
    }

    return {
        columns,
        rows,
        rowsRolledOver
    };
};

//This includes old Tech endpoint with CarryOver. Low performance.
export const fetchDashboardDynamicFields = async (dashboardDynamicFieldsRequestDto) => {
    try {
        // await here is necessary to catch error properly
        // error occurs when settings for dynamic fields are not present in backend for user/company/department
        const dynamicFields = await dashboardService.getDashboardDynamicFields(
            dashboardDynamicFieldsRequestDto,
        );
        return dynamicFields;
    } catch (error) {
        if (error.status === 500 || error.status === 403) {
            console.log("User not authorized to render [DashboardTimeLog]");
        } else {
            console.log(error);
        }
        return null;
    }
};

//This doesn't includes old Tech endpoint with CarryOver. Better performance.
export const fetchDashboardDynamicFieldsNEW = async (dashboardDynamicFieldsRequestDto) => {
    try {
        const dynamicFields = await dashboardService.getDashboardDynamicFieldsNEW(
            dashboardDynamicFieldsRequestDto,
        );
        return dynamicFields;
    } catch (error) {
        if (error.status === 500 || error.status === 403) {
            console.log("User not authorized to render [DashboardTimeLog]");
        } else {
            console.log(error);
        }
        return null;
    }
};

export const fetchDashboardCarryOverDynamicFields = async (dashboardDynamicFieldsRequestDto) => {
    try {
        const dynamicFields = await dashboardService.getDashboardCarryOverDynamicFields(
            dashboardDynamicFieldsRequestDto,
        );
        return dynamicFields;
    } catch (error) {
        if (error.status === 500 || error.status === 403) {
            console.log("User not authorized to render [DashboardTimeLog]");
        } else {
            console.log(error);
        }
        return null;
    }
};

export const filterDynamicDataById = ({ idField, id, data }) => {
    return data.map((dynamicFieldsWithOwner) => ({
        ...dynamicFieldsWithOwner,
        DynamicFields: dynamicFieldsWithOwner.DynamicFields.map((field) => ({
            ...field,
            DynamicDatas: field.DynamicDatas.filter((dynamicData) => dynamicData[idField] === id),
        }))
    }))
    .filter((f) => {
        if(idField == "UserId"){
            for(let i = 0; i < f?.Owner?.Members.length; i++){
                if(f?.Owner?.Members[i]?.UserId == id){
                    return true;
                }
            }
        }
        else if(idField == "ProjectId"){
            if(f?.Owner?.Projects?.includes(id)){
                return true;
            }
        }
        else{
            return true;
        }
    }) ;
};

const formatTimeDynamicData = ({ sortType, DynamicDatas, customDates }) => {
    let hours = 0;

    for (const data of DynamicDatas) {
        if (data?.DynamicData?.Data != null && data?.DynamicData?.Data !== undefined) {
            const [HH, MM] = data?.DynamicData?.Data?.split(':')?.map(Number);
            hours += HH;
        }
    }

    return `${hours}h`;
};

const getDateBeforeDays = (date, days) => {
    const lastWeek = new Date(date);
    lastWeek.setDate(lastWeek.getDate() - days);
    return lastWeek;
};

const compareTimeData = (timeA, timeB) => {
    if (timeA === cellPlaceholder && timeB !== cellPlaceholder) return -1;
    if (timeA !== cellPlaceholder && timeB === cellPlaceholder) return 1;
    if (timeA === cellPlaceholder && timeB === cellPlaceholder) return 0;

    const [timeAHours, tempA] = timeA.split('h');
    const [timeAMinutes] = tempA.split('m')[0].trim();

    const [timeBHours, tempB] = timeB.split('h');
    const [timeBMinutes] = tempB.split('m')[0].trim();

    const a = {
        hours: Number(timeAHours),
        minutes: Number(timeAMinutes),
    };

    const b = {
        hours: Number(timeBHours),
        minutes: Number(timeBMinutes),
    };

    if (a.hours < b.hours) return -1;
    if (a.hours > b.hours) return 1;
    if (a.minutes < b.minutes) return -1;
    if (a.minutes > b.minutes) return 1;
    return 0;
};

/**
 * Code for generating "Last Week" column if needed:
 * const lastWeekRows = [];
 * columns.push(ownerType, ...fields.map((field) => field.Label), 'Last Week', 'Total Hours');
 *                      DynamicDataWithComment.forEach((dataWithComment) => {
                            const createdDate = new Date(dataWithComment.TaskComment.Created);
                            const currentDate = new Date();
                            if (
                                createdDate.getTime() >= getDateBeforeDays(currentDate, 14) &&
                                createdDate.getTime() <= getDateBeforeDays(currentDate, 7)
                            ) {
                                lastWeekRows.push(dataWithComment);
                            }
                        });
 *              cells.push(
                    formatTimeDynamicData(SORT_TYPES.allTime, lastWeekRows)
                );
 *
 */
